import { AlertTriangle, Inbox, Loader2, Lock, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/api-error';
import { cn } from './cn';

/**
 * Estados visuales obligatorios (spec 49).
 *
 * La spec es explicita: nunca mostrar una pantalla vacia mientras se carga.
 * Se agrupan aqui para que ninguna pantalla pueda "olvidarse" de uno: el
 * componente <AsyncBoundary> obliga a resolver los cuatro casos.
 */

interface StateShellProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly tone?: 'neutral' | 'danger';
  readonly className?: string;
}

function StateShell({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  className,
}: StateShellProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex size-11 items-center justify-center rounded-md border',
          tone === 'danger'
            ? 'border-st-out/25 bg-st-out/8 text-st-out'
            : 'border-line bg-sunken text-fg-subtle',
        )}
        aria-hidden="true"
      >
        {icon}
      </div>

      <div className="max-w-sm space-y-1">
        <p className="text-base font-medium text-fg-body">{title}</p>
        {description !== undefined && (
          <p className="text-sm leading-relaxed text-fg-muted">{description}</p>
        )}
      </div>

      {action}
    </div>
  );
}

/** Carga en curso. Se anuncia a lectores de pantalla via aria-live. */
export function LoadingState({ label = 'Cargando informacion' }: { label?: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 px-6 py-14"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="size-5 animate-spin text-brand-ink" aria-hidden="true" />
      <p className="text-sm text-fg-muted">{label}</p>
    </div>
  );
}

/**
 * Esqueleto de tabla.
 *
 * Se prefiere a un spinner cuando ya se conoce la forma del contenido: evita
 * el salto de layout al llegar los datos, que cuenta como CLS (spec 52).
 */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-px" aria-hidden="true">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 px-4 py-2.5">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <div
              key={columnIndex}
              className="h-3.5 flex-1 animate-pulse rounded-xs bg-sunken"
              style={{
                // Anchos irregulares: un esqueleto de columnas identicas se lee
                // como un error de renderizado, no como contenido en camino.
                maxWidth: columnIndex === 0 ? '22%' : `${58 + ((columnIndex * 13) % 30)}%`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title = 'Sin resultados',
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <StateShell
      icon={<Inbox className="size-5" />}
      title={title}
      {...(description === undefined ? {} : { description })}
      {...(action === undefined ? {} : { action })}
    />
  );
}

/**
 * Error de carga.
 *
 * Muestra el requestId siempre que exista: es lo unico que permite al soporte
 * localizar la traza exacta en los logs (spec 58).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null;

  if (apiError?.isForbidden === true) {
    return (
      <StateShell
        icon={<Lock className="size-5" />}
        title="No tiene permiso para ver esta informacion"
        description="Solicitar acceso al administrador del sistema si considera que es un error."
      />
    );
  }

  return (
    <StateShell
      tone="danger"
      icon={<AlertTriangle className="size-5" />}
      title="No se pudo cargar la informacion"
      description={apiError?.message ?? 'Ocurrio un error inesperado.'}
      action={
        <div className="flex flex-col items-center gap-2">
          {onRetry !== undefined && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-sm border border-line-strong bg-panel px-3 py-1.5 text-sm font-medium text-fg-body transition-colors duration-(--t-tap) hover:bg-sunken"
            >
              Reintentar
            </button>
          )}
          {apiError !== null && apiError.requestId !== 'local' && (
            <p className="font-mono text-2xs text-fg-subtle">Referencia: {apiError.requestId}</p>
          )}
        </div>
      }
    />
  );
}

export function ForbiddenState() {
  return (
    <StateShell
      icon={<ShieldAlert className="size-5" />}
      title="Acceso denegado"
      description="Su rol no incluye el permiso necesario para esta operacion."
    />
  );
}

interface AsyncBoundaryProps<T> {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly onRetry?: () => void;
  readonly loadingFallback?: ReactNode;
  readonly emptyFallback?: ReactNode;
  readonly isEmpty?: (data: T) => boolean;
  readonly children: (data: T) => ReactNode;
}

/**
 * Resuelve los cuatro estados de una consulta en un solo lugar.
 *
 * Obliga a decidir que se muestra mientras carga, si falla y si no hay datos.
 * Es la forma practica de cumplir la spec 49 sin repetir el mismo bloque de
 * condicionales en cada pantalla.
 */
export function AsyncBoundary<T>({
  isLoading,
  error,
  data,
  onRetry,
  loadingFallback,
  emptyFallback,
  isEmpty,
  children,
}: AsyncBoundaryProps<T>) {
  if (isLoading) return <>{loadingFallback ?? <LoadingState />}</>;
  if (error !== null && error !== undefined) {
    return <ErrorState error={error} {...(onRetry === undefined ? {} : { onRetry })} />;
  }
  // `??` no vale aqui: convertiria un `null` deliberado -"cuando no haya
  // nada, no pintes nada"- en el estado vacio por defecto, y quien lo quiera
  // de verdad tiene que pasar un fragmento vacio para enganar al operador.
  // Solo la AUSENCIA de la prop pide el estado por defecto.
  const vacio = emptyFallback === undefined ? <EmptyState /> : emptyFallback;

  if (data === undefined) return <>{vacio}</>;
  if (isEmpty?.(data) === true) return <>{vacio}</>;

  return <>{children(data)}</>;
}
